import * as React from "react";
import { useState, useEffect } from "react";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCard } from "../../../../utils/reactQuery/cards";
import { moveWorklog } from "../../../../sevices/apiWorklog";
import { objectToFormData } from "../../../../utils";
import { clearCheckedIds } from "../../../../redux/CheckboxSclice";
import { useCustomSearchParams } from "../../../../hooks/useCustomSearchParams";
import useToastPromise from "../../../../hooks/useToast";
import FormDialog from "../../../../components/Dialog/FormDialog";
import useMutate from "../../../../hooks/useMutate";
import useBoardIdByKey from "../../../../hooks/useBoardIdByKey";

export default React.memo(function MoveWorklog({ cardId, disabled = false }) {
  const [cardIdInput, setCardIdInput] = useState("");
  const [targetCard, setTargetCard] = useState(null);
  const [newCardId, setNewCardId] = useState(null);
  const [confirmationMoveWorklog, setConfirmationMoveWorklog] = useState(false);
  const { id } = useBoardIdByKey();
  const [brd_id] = useCustomSearchParams("brd_id");
  const [crd_id] = useCustomSearchParams("card_id");
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const selectedIds = useSelector((state) => state.checkbox.checkedIds);
  const { card, isLoading } = useCard(cardIdInput);

  // Check if any worklogs are selected
  const isWorklogSelected = useMemo(() => selectedIds.length > 0, [selectedIds]);

  useEffect(() => {
    if (card && card?.data) {
      setTargetCard([card?.data?.data?.responseData]);
    } else {
      setTargetCard(null);
    }
  }, [card]);

  const handleConfirmationMoveWorklog = () => {
    setConfirmationMoveWorklog(false);
  };

  const { mutate: MoveWorklogMutate } = useMutate(moveWorklog, [`teams`, `watching`, `mineCards`],
    () => { },
    () => {
      queryClient.invalidateQueries({
        queryKey: [`worklog`, cardId ?? crd_id],
      });
      queryClient.invalidateQueries({
        queryKey: [`card`, cardId ?? Number(crd_id)],
      });
      queryClient.invalidateQueries({
        queryKey: [`cards`, id ?? Number(brd_id)],
      });
      queryClient.invalidateQueries({ queryKey: [`teams`] });
      queryClient.invalidateQueries({ queryKey: [`watching`] });
      queryClient.invalidateQueries({ queryKey: [`mineCards`] });
      dispatch(clearCheckedIds());
      setTargetCard(null);
      handleConfirmationMoveWorklog();
    },
    () => {
      handleConfirmationMoveWorklog();
    },
  );

  const callback = useCallback(() => {
    const data = objectToFormData({
      existing_card_id: cardId,
      new_card_id: newCardId,
      card_log_ids: selectedIds,
    });
    MoveWorklogMutate(data);
  }, [cardId, newCardId, selectedIds, MoveWorklogMutate]);

  const optionValues = useMemo(() => {
    return targetCard ? targetCard : [];
  }, [targetCard]);

  const handleInputId = (Id) => {
    setCardIdInput(Id);
  };
  const debounceSearch = debounce(handleInputId, 1000);

  return (
    <>
      {confirmationMoveWorklog && (
        <FormDialog
          open={confirmationMoveWorklog}
          handleClose={handleConfirmationMoveWorklog}
          loading={isLoading}
          handleSubmit={callback}
          buttonTxt="Submit"
          title="Confirmation: Move Confirmation"
          alignActions="center"
          cancelBtn={true}
          fullWidth={true}>
          <Typography className="align_text_center mb-2">
            Are you sure you want to Move Worklogs?
          </Typography>
        </FormDialog>
      )}
      <FormControl className="checkbox-outerbox" size="small" fullWidth>
        <Autocomplete
          options={optionValues}
          getOptionLabel={(option) => option.title}
          onInputChange={(event, newValue) => {
            debounceSearch(newValue);
          }}
          filterOptions={(optionValues, state) => {
            return optionValues.filter(
              (option) =>
                option?.card_id?.toString()?.includes(state.inputValue) // Search by id
            );
          }}
          onChange={(event, newValue) => {
            setNewCardId(newValue?.card_id);
            setConfirmationMoveWorklog(true);
          }}
          disabled={disabled || !isWorklogSelected}
          loading={isLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Enter task ID"
              size="small"
              helperText={isLoading ? "Loading..." : !isWorklogSelected ? "Please select at least one worklog" : ""}
            />
          )}
        />
      </FormControl>
    </>
  );
})
